import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { RemarksSection } from '../../../components/ui/remarks/Remarks';
import AIGeneratedBadge from '../../../components/ui/pill/AiGeneratedBadge';
import { AnalysisRemarkKeys, REMARK_DISPLAY_CONFIG } from '../../remarks/config';
import { DEFAULT_REMARKS_ICON } from '../config';
import { type MDXEditorMethods } from '../../../components/ui/editor';


interface AIRemarksContainerProps {
    remarkKey: AnalysisRemarkKeys;
    initialValue?: string;
    titleTextOverride?: string;
    disableGenerate?: boolean;
    onSave?: (newText: string) => void;
    onImprove: (currentText: string) => Promise<string | null>;
    onGenerate: () => Promise<string | undefined>;
}

const AIRemarksContainer: React.FC<AIRemarksContainerProps> = ({ 
  remarkKey, 
  initialValue = '', 
  titleTextOverride,
  disableGenerate = false,
  onSave,
  onImprove,
  onGenerate,
}) => {
    
    const [text, setText] = useState(initialValue);
    const [error, setError] = useState<string | null>(null);
    const [isLocalImproving, setIsLocalImproving] = useState(false);
    const [isLocalSaving, setIsLocalSaving] = useState(false);
    const [isLocalGenerating, setIsLocalGenerating] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const editorRef = useRef<MDXEditorMethods>(null);

    // --- SYNCHRONIZATION: Sync local state when the initialValue prop changes ---
    useEffect(() => {
        setText(initialValue);
        setHasChanges(false);
    }, [initialValue]);

    const { title, placeholder, icon } = REMARK_DISPLAY_CONFIG[remarkKey];
    const resolvedTitle = titleTextOverride ?? title;
    const styledTitle = useMemo(() => {

        return (
            <div className="flex items-center">
               <p className="font-sans font-semibold text-sm leading-sm tracking-normal text-[#737373]">
                    {resolvedTitle}
               </p>
                {/* <AIGeneratedBadge /> uncomment during AI integration */}
            </div>
        );
    }, [resolvedTitle, icon]);

    // const aiGeneratedBadge = useMemo(() => <AIGeneratedBadge />, []);

    const handleChange = (value: string) => {
        setText(value);
        setHasChanges(value !== initialValue);
        if (error) setError(null);
    };

    const handleImprove = async () => {
        if (!onImprove) {
            return;
        }
        
        setIsLocalImproving(true);
        setError(null);
        try {
            const newText = await onImprove(text);
            if (newText) {
                setText(newText);
                editorRef.current?.setMarkdown(newText);
                setHasChanges(newText !== initialValue);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to improve text.');
        } finally {
            setIsLocalImproving(false);
        }
    };
  
    const handleSave = async () => {
        if (onSave) {
            setIsLocalSaving(true);
            setError(null);
            try {
                await onSave(text); // Await the parent's onSave function
                setHasChanges(false);
            } catch (err: any) {
                setError(err.message || 'Failed to save.');
            } finally {
                setIsLocalSaving(false); // Set loading to FALSE when finished
            }
        }
    };

     const handleGenerate = async () => {
      if (onGenerate) {
        setIsLocalGenerating(true);
        setError(null);
        try {
            const newText = await onGenerate();
            if (newText) {
                setText(newText);
                editorRef.current?.setMarkdown(newText);
                setHasChanges(newText !== initialValue);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate text.');
        } finally {
            setIsLocalGenerating(false);
        }
    }
    };


    return (
      <RemarksSection
        ref={editorRef}
        remarkKey={remarkKey}
        value={text}
        onChange={handleChange}
        onGenerateAI={disableGenerate ? undefined : handleGenerate}
        onImproveAI={handleImprove}
        onSave={onSave ? handleSave : undefined} // Only show save if onSave is provided
        isGenerating={isLocalGenerating}
        isImproving={isLocalImproving}
        isSaving={isLocalSaving}
        error={error}
        title={styledTitle}
        remarkType="general"
        placeholder={placeholder}
        sectionProps={{ className: "" }}
        hasUnsavedChanges={hasChanges}
      />
    );
};
  
export { AIRemarksContainer };